const MODULE_NAME = "_10_most_popular_times";

import React, { useEffect, useState } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { cachedGet } from "./apiCache.js";
import { useModuleVisibility } from "./useModuleVisibility.js";

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
    useEffect(() => {
        function handleResize() {
            setIsMobile(window.innerWidth <= 700);
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return isMobile;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function MostPopularTimes({ refreshKey }) {
    // DECLARE ALL HOOKS FIRST - Before any conditional logic
    const [chartData, setChartData] = useState(null);
    const [tooltip, setTooltip] = useState("Most active trading times by half-hour interval");
    const [collapsed, setCollapsed] = useState(true);
    const [availableDates, setAvailableDates] = useState([]);
    const [date1, setDate1] = useState("");
    const [date2, setDate2] = useState("");
    const [date2Options, setDate2Options] = useState([]);

    const isMobile = useIsMobile();

    // On mount or when availableDates changes, set date1 from sessionStorage.navigationDate if available
    useEffect(() => {
        cachedGet(`${process.env.REACT_APP_API_URL}/api/user-trade-dates`)
            .then(res => {
                const dates = (res.data.dates || []).sort((a, b) => b.localeCompare(a));
                setAvailableDates(dates);

                // Use navigationDate from sessionStorage if available and valid
                const navDate = sessionStorage.getItem("navigationDate");
                let validNavDate = navDate && dates.includes(navDate) ? navDate : dates[0] || "";

                setDate1(validNavDate);

                // If navigationDate is present and not the latest, use the date before it for date2
                if (validNavDate && dates.length > 1) {
                    const navIdx = dates.indexOf(validNavDate);
                    // Use the next date in the sorted array (which is the prior date chronologically)
                    const priorDate = navIdx >= 0 && navIdx + 1 < dates.length ? dates[navIdx + 1] : dates[1] || "";
                    setDate2(priorDate);
                } else {
                    setDate2(dates[1] || "");
                }
            });
    }, [refreshKey]);

    useEffect(() => {
        if (availableDates.length === 0) {
            setDate2Options([]);
            return;
        }
        const dateOptions = availableDates.filter(date => date !== date1);
        const options = [
            ...dateOptions,
            "Last 7 days",
            "Last 30 days"
        ];
        setDate2Options(options);
        if (!options.includes(date2)) {
            setDate2(options[0] || "");
        }
    }, [availableDates, date1]);

    useEffect(() => {
        if (!date1 || !date2) return;
        cachedGet(`${process.env.REACT_APP_API_URL}/api/most-popular-times`, {
            date1,
            date2
        })
            .then(res => {
                let chart = res.data.chart;
                function averageDataset(dataset, rangeLabel) {
                    if (rangeLabel === "Last 7 days") return dataset.map(v => Math.round(v / 7));
                    if (rangeLabel === "Last 30 days") return dataset.map(v => Math.round(v / 30));
                    return dataset;
                }
                if (chart && chart.data && Array.isArray(chart.data.datasets)) {
                    chart.data.datasets = chart.data.datasets.map(ds => {
                        if (ds.label === "Last 7 days" || ds.label === "Last 30 days") {
                            return {
                                ...ds,
                                data: averageDataset(ds.data, ds.label)
                            };
                        }
                        return ds;
                    });
                    if (chart.data.datasets.some(ds => ds.label === "Last 7 days" || ds.label === "Last 30 days")) {
                        chart.options.plugins.title.text += " (Averages per day for ranges)";
                    }
                }
                const options = {
                    ...chart.options,
                    maintainAspectRatio: false,
                    responsive: false,
                };
                setChartData({
                    data: chart.data,
                    options
                });
                setTooltip(res.data.tooltip);
            });
    }, [date1, date2]);

    // Check visibility based on module_name URL parameter - AFTER all other hooks
    const isVisible = useModuleVisibility(MODULE_NAME);

    // Return null if component is not visible
    if (!isVisible) return null;

    function formatDateDisplay(dateStr) {
        if (!dateStr || typeof dateStr !== "string" || dateStr.length < 10) return dateStr;
        const [year, month, day] = dateStr.split("-");
        return `${month}-${day}-${year.slice(2)}`;
    }

    if (!availableDates.length) return <div>Loading available trade dates...</div>;
    if (!chartData) return <div>Loading trading times histogram...</div>;

    const chartWidth = isMobile ? 340 : 800;
    const chartHeight = isMobile ? 220 : 400;

    return (
        <div className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`} data-analytics={MODULE_NAME}>
            <h4
                className="collapse-toggle-headline"
                onClick={() => setCollapsed(c => !c)}
            >
                📊 Most active trading times
                <span
                    className="tooltip-icon"
                    style={{
                        marginLeft: 8,
                        verticalAlign: "middle",
                        cursor: "pointer",
                        color: "#888",
                        fontSize: 18
                    }}
                    title={tooltip}
                >
                    🛈
                </span>
            </h4>
            <div className="collapse-content">
                {/* Date comparison dropdowns */}
                <div style={{ display: "flex", gap: 24, marginBottom: 24, marginTop: 8 }}>
                    <div>
                        <label htmlFor="date1-select" style={{ fontWeight: "bold", marginRight: 8 }}>Date 1:</label>
                        <select
                            id="date1-select"
                            value={date1}
                            onChange={e => setDate1(e.target.value)}
                            className="dropdown-curved"
                        >
                            {availableDates.length > 0 && (
                                <option value={availableDates[0]}>
                                    {formatDateDisplay(availableDates[0])} (Latest)
                                </option>
                            )}
                            <option value="Last 7 days">Last 7 days</option>
                            <option value="Last 30 days">Last 30 days</option>
                            {availableDates.map(date =>
                                date !== availableDates[0] ? (
                                    <option key={date} value={date}>
                                        {formatDateDisplay(date)}
                                    </option>
                                ) : null
                            )}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="date2-select" style={{ fontWeight: "bold", marginRight: 8 }}>Date 2:</label>
                        <select
                            id="date2-select"
                            value={date2}
                            onChange={e => setDate2(e.target.value)}
                            className="dropdown-curved"
                        >
                            {date2Options.map(opt => (
                                <option key={opt} value={opt}>
                                    {opt === "Last 7 days" || opt === "Last 30 days" ? opt : formatDateDisplay(opt)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="popular-times-chart-container">
                    <Bar
                        data={chartData.data}
                        options={chartData.options}
                        width={chartWidth}
                        height={chartHeight}
                    />
                </div>
            </div>
        </div>
    );
}